import { Resume } from '@sr/types';
import React from 'react';
import {
  Box,
  ContentWithSidebar,
  CrossIcon,
  ListIcon,
  TUILink,
} from '../../ui';
import HelpButton from '../help/HelpButton';
import Review from '../review-messages/ReviewMessages';
import AboutForm from './AboutForm';
import BlockListForm from './BlockListForm';
import DetailForm from './DetailForm';
import InlineListForm from './InlineListForm';
import MarkdownUpgradePrompt from './MarkdownUpgradePrompt';
import Sidebar from './Sidebar';
import { useMessages } from 'v1/hooks/use-messages';

const Write = ({ resume }: { resume: Resume }) => {
  useMessages(resume.owner, resume.id);
  return (
    <ContentWithSidebar
      openButtonContents={<OpenButtonContents />}
      closeButtonContents={<CrossIcon color="white" />}
      sidebarContent={<Sidebar resume={resume} />}
      mainContent={<Forms resume={resume} />}
      rightSidebarContent={<RightSidebar />}
    />
  );
};

const Forms = ({ resume }: { resume: Resume }) => (
  <>
    <Box sx={{ maxWidth: [null, null, 640], width: '100%' }}>
      <MarkdownUpgradePrompt resume={resume} />
      <AboutForm resume={resume} />

      {(resume.sectionsOrder || []).map((sectionId) => {
        const section = resume.sections[sectionId];
        if (!section) {
          throw new Error(`Missing section for id: ${sectionId}`);
        }
        switch (section.type) {
          case 'detail':
            return (
              <DetailForm
                resume={resume}
                section={section}
                key={section.sectionId}
              />
            );
          case 'inline-list':
            return (
              <InlineListForm
                resume={resume}
                section={section}
                key={section.sectionId}
              />
            );
          case 'block-list':
            return (
              <BlockListForm
                resume={resume}
                section={section}
                key={section.sectionId}
              />
            );
          default:
            console.error('Section found with invalid type.', section);
            throw new Error('Section found with invalid type.');
        }
      })}
    </Box>
    <HelpButton userId={resume.owner} />
  </>
);

function RightSidebar() {
  const blueText = {
    backgroundColor: 'blue',
    color: 'white',
    lineHeight: 1,
    fontWeight: 'bold',
    fontSize: 28,
    px: 1,
  };
  const linkStyle = {
    color: 'blue',
    // fontWeight: 'bold',
    textDecoration: 'none',
    marginTop: 2,
    ':hover': { textDecoration: 'underline' },
  };
  return (
    <div>
      <Review />
      <Box
        sx={{
          marginTop: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          bg: 'white',
          padding: '14px 12px 16px 14px',
        }}
      >
        <Box sx={blueText}>The</Box>
        <Box sx={blueText}>Resume</Box>
        <Box sx={blueText}>Handbook</Box>
        <Box
          sx={{ fontSize: 14, lineHeight: 1.5, mt: 3, mb: 2, color: 'black70' }}
        >
          Wisdom from 8 years of helping over 200,000 people create a winning
          resume, combined with insights from top recruiters and hiring
          managers.
        </Box>
        <TUILink
          href="https://learn.standardresume.co/write/style"
          target="_blank"
          sx={linkStyle}
        >
          Resume Writing Style →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co/optimize/understand-the-ats"
          target="_blank"
          sx={linkStyle}
        >
          Understand the ATS →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co/write/work-experience"
          target="_blank"
          sx={linkStyle}
        >
          Work Experience →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co/write/education"
          target="_blank"
          sx={linkStyle}
        >
          Education →
        </TUILink>
        <TUILink
          href="https://learn.standardresume.co"
          target="_blank"
          sx={linkStyle}
        >
          Overview →
        </TUILink>
      </Box>
    </div>
  );
}

function OpenButtonContents() {
  return (
    <>
      <ListIcon pr={2} color="white" /> Sections
    </>
  );
}

export default Write;
